import { useState } from 'react';
import { Banner, Button, Card, Heading, Spinner, Text, VStack } from '@astryxdesign/core';
import { useSearchParams } from 'react-router';

import { api } from '../lib/api';
import { messageOf } from '../stores/auth';

type Status = 'idle' | 'pending' | 'deleted' | 'error';

/**
 * The "this wasn't me" link in the V1 email. Deliberately requires an explicit
 * click here rather than firing on page load — an email client's link-scanner
 * prefetching the mail must not be able to delete the account on its own.
 */
export function CancelSignupPage() {
  const [params] = useSearchParams();
  const uid = params.get('uid') ?? '';
  const token = params.get('token') ?? '';
  const missing = !uid || !token;

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onConfirm() {
    setStatus('pending');
    setError(null);
    try {
      await api.cancelSignup(uid, token);
      setStatus('deleted');
    } catch (err) {
      setStatus('error');
      setError(messageOf(err));
    }
  }

  return (
    <Card padding={4} variant="default" className="auth-card">
      <VStack gap={3}>
        <Heading level={1}>Didn&apos;t create this account?</Heading>

        {missing && (
          <Banner status="error" title="Invalid link" description="This link is missing information." />
        )}

        {!missing && status === 'idle' && (
          <>
            <Text size="sm" color="secondary">
              If someone used your email address to create a VecTime account without your
              consent, confirm below to cancel the request and permanently delete it. This
              only works while the account has never been confirmed.
            </Text>
            <Button label="Delete this account" variant="destructive" onClick={onConfirm} width="100%" />
          </>
        )}

        {status === 'pending' && <Spinner size="lg" label="Deleting account" />}

        {status === 'deleted' && (
          <Banner
            status="success"
            title="Account deleted"
            description="The account tied to this link has been removed."
          />
        )}

        {status === 'error' && (
          <Banner
            status="error"
            title="Could not delete this account"
            description={error ?? 'This link is invalid or has expired.'}
          />
        )}
      </VStack>
    </Card>
  );
}
